import dayjs, {Dayjs} from "dayjs";
import 'dayjs/locale/fr';
import {AbsenceStatus, TeamAbsence} from "@/core/interfaces/TeamPlanning";

/** A motive offered on absence creation, with the colors of its chip and bar. */
export interface AbsenceMotif {
    label: string;
    /** Tailwind background and text classes, charte tints. */
    classes: string;
}

/**
 * The offered motives. The server stores a free string, and it stores the
 * label itself: renaming one here orphans the absences already saved under
 * its former wording, which then read as "Autre" and show their stored text
 * as-is. Add rather than rename.
 */
export const ABSENCE_MOTIFS: AbsenceMotif[] = [
    {label: 'Congés payés', classes: 'bg-primary/10 text-primary'},
    {label: 'RTT', classes: 'bg-lightPurple-lightest text-lightPurple-darkest'},
    {label: 'Maladie', classes: 'bg-red-light/15 text-red-light'},
    {label: 'Formation', classes: 'bg-orange-light text-gray-dark'},
    {label: 'Congé maternité', classes: 'bg-pink/20 text-gray-dark'},
    {label: 'Congé sans solde', classes: 'bg-apodisPink text-darkPurple'},
    {label: 'Autre', classes: 'bg-neutral text-gray-dark'}
];

/** The catch-all motive, which carries any free-text reason. */
export const OTHER_MOTIF: AbsenceMotif = ABSENCE_MOTIFS[ABSENCE_MOTIFS.length - 1];

/** Motives a form offers as a choice: the free-text one is not one of them. */
export const CHOOSABLE_MOTIFS: AbsenceMotif[] = ABSENCE_MOTIFS.filter(motif => motif !== OTHER_MOTIF);

/**
 * The motive of a stored reason, "Autre" when it matches none — an absence
 * saved before this catalog existed, or a free reason typed by the user.
 */
export const motifOf = (reason: string): AbsenceMotif => {
    const normalized = reason.trim().toLocaleLowerCase('fr');

    return ABSENCE_MOTIFS.find(motif => motif.label.toLocaleLowerCase('fr') === normalized) ?? OTHER_MOTIF;
};

/**
 * Statuses worth a badge. `approved` has none on purpose: every absence
 * recorded from the counter is granted, so badging them all would say
 * nothing. Only the rows left by the former request flow stand out.
 */
export const ABSENCE_STATUS_BADGES: Partial<Record<AbsenceStatus, { label: string; classes: string }>> = {
    pending: {label: 'En attente', classes: 'bg-orange-light text-gray-dark'},
    refused: {label: 'Refusée', classes: 'bg-red-light/15 text-red-light'}
};

/** Days covered by the absence, both bounds included. */
export const absenceDurationDays = (absence: TeamAbsence): number =>
    dayjs(absence.ends_at).diff(dayjs(absence.starts_at), 'day') + 1;

export const formatAbsenceDate = (date: string): string =>
    dayjs(date).locale('fr').format('D MMM YYYY');

/**
 * The month a timeline shows. Explicitly French, like every format here: the
 * renderer never sets a global locale — `dayjs.locale('fr')` lives in the
 * Electron main process, which cannot configure this side.
 */
export const formatAbsenceMonth = (anchor: Dayjs): string =>
    anchor.locale('fr').format('MMMM YYYY');

export const formatAbsenceRange = (absence: TeamAbsence): string =>
    `Du ${formatAbsenceDate(absence.starts_at)} au ${formatAbsenceDate(absence.ends_at)}`;

export const formatAbsenceDuration = (absence: TeamAbsence): string => {
    const days = absenceDurationDays(absence);

    return days > 1 ? `${days} jours` : '1 jour';
};
